/**
 * MARS Portfolio UI state (Zustand).
 *
 * Holds selections that are not server-owned: fund, toggles, selected tab,
 * timeframe, performance period. Server data itself stays in React Query —
 * we only keep what the UI needs across components.
 */
import { create } from 'zustand';
import type { FundId, PerformancePeriod, RiskPeriod, Timeframe } from './types';

export type DashboardTab = 'asset-positions' | 'risk-metrics' | 'performance';
export type RiskView = 'absolute' | 'contribution';

export interface MarsPortfolioState {
  fund: FundId;
  tab: DashboardTab;
  selectedAssets: string[];
  /** `true` = Vol Timing ON. */
  volTiming: boolean;
  /** `true` = Constraints ON. */
  constraints: boolean;
  /** `true` = Transaction costs ON. */
  tc: boolean;
  timeframe: Timeframe;
  riskPeriod: RiskPeriod;
  /** `absolute` (portfolio) vs `contribution` (risk contribution). */
  riskView: RiskView;
  performancePeriod: PerformancePeriod;
  customStartDate: string | null;
  customEndDate: string | null;

  setFund: (fund: FundId) => void;
  setTab: (tab: DashboardTab) => void;
  toggleAsset: (asset: string) => void;
  setSelectedAssets: (assets: string[]) => void;
  setVolTiming: (value: boolean) => void;
  setConstraints: (value: boolean) => void;
  setTc: (value: boolean) => void;
  setTimeframe: (value: Timeframe) => void;
  setRiskPeriod: (value: RiskPeriod) => void;
  setRiskView: (value: RiskView) => void;
  setPerformancePeriod: (value: PerformancePeriod) => void;
  setCustomDates: (start: string | null, end: string | null) => void;
}

/** "Asset Positions" default selection (Equities). */
const DEFAULT_SELECTED_ASSETS = ['Equities'];
const MAX_ASSETS = 4;

export const useMarsPortfolioStore = create<MarsPortfolioState>((set) => ({
  fund: 'mars-fqm',
  tab: 'asset-positions',
  selectedAssets: DEFAULT_SELECTED_ASSETS,
  volTiming: true,
  constraints: true,
  tc: true,
  timeframe: '1y',
  riskPeriod: '3y',
  riskView: 'absolute',
  performancePeriod: 'since_inception',
  customStartDate: null,
  customEndDate: null,

  setFund: (fund) => set({ fund }),
  setTab: (tab) => set({ tab }),
  toggleAsset: (asset) =>
    set((state) => {
      if (state.selectedAssets.includes(asset)) {
        return { selectedAssets: state.selectedAssets.filter((a) => a !== asset) };
      }
      if (state.selectedAssets.length >= MAX_ASSETS) return state;
      return { selectedAssets: [...state.selectedAssets, asset] };
    }),
  setSelectedAssets: (assets) => set({ selectedAssets: assets.slice(0, MAX_ASSETS) }),
  setVolTiming: (value) => set({ volTiming: value }),
  setConstraints: (value) => set({ constraints: value }),
  setTc: (value) => set({ tc: value }),
  setTimeframe: (value) => set({ timeframe: value }),
  setRiskPeriod: (value) => set({ riskPeriod: value }),
  setRiskView: (value) => set({ riskView: value }),
  setPerformancePeriod: (value) => set({ performancePeriod: value }),
  setCustomDates: (start, end) => set({ customStartDate: start, customEndDate: end }),
}));

export const MAX_ASSET_SELECTIONS = MAX_ASSETS;
